import React, { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AdminSettings } from "@/types/adminSettings";
import { ServiceWorkerDebug } from "./ServiceWorkerDebug";
import {
  Smartphone, 
  Key, 
  FileEdit, 
  Copy, 
  CheckCircle, 
  Bell, 
  Upload, 
  Image, 
  Bug, 
  AlertCircle, 
  CheckCircle2, 
  XCircle, 
  RefreshCw,
  Search,
  Shield
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePWAIconUpload } from "@/hooks/usePWAIconUpload";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { usePWAIconValidation } from "@/hooks/usePWAIconValidation";
import { useDynamicPWA } from "@/hooks/useDynamicPWA";
import { usePWAIconGenerator } from "@/hooks/usePWAIconGenerator";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface PWATabProps {
  settings: AdminSettings | null;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => void;
  isSaving: boolean;
}

export const PWATab = ({ settings, updateSettings, saveSettings, isSaving }: PWATabProps) => {
  const [isGeneratingKeys, setIsGeneratingKeys] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [manifestData, setManifestData] = useState<any>(null);
  const [swStatus, setSWStatus] = useState<any>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isSendingTestPush, setIsSendingTestPush] = useState(false);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(false);
  const [isTestingSigning, setIsTestingSigning] = useState(false);
  const [hasSupabaseVAPIDKeys, setHasSupabaseVAPIDKeys] = useState<boolean | null>(null);
  const [generationProgress, setGenerationProgress] = useState<{ current: number; total: number } | null>(null);
  const [registeredDevices, setRegisteredDevices] = useState<any[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isSendingToSelected, setIsSendingToSelected] = useState(false);
  const [deviceDiagnostics, setDeviceDiagnostics] = useState<string[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();
  const { uploadIcon, isUploading } = usePWAIconUpload();
  const { isInstallable, isInstalled, triggerInstall } = usePWAInstall();
  const { validateIcon, validatePWAIconSet, isValidating } = usePWAIconValidation();
  const { refreshPWAAssets } = useDynamicPWA();
  const { generateMissingIcons, isGenerating } = usePWAIconGenerator();
  
  // Check for VAPID keys in Supabase secrets on component mount
  React.useEffect(() => {
    const checkSupabaseVAPIDKeys = async () => {
      try {
        // Check if user has push subscriptions instead of making invalid test calls
        const { data: subscriptions, error } = await supabase
          .from('push_subscriptions')
          .select('id')
          .eq('user_id', user?.id)
          .limit(1);
        
        if (error) {
          console.error('Error checking push subscriptions:', error);
          setHasSupabaseVAPIDKeys(false);
          return;
        }
        
        // If we can query subscriptions successfully, VAPID keys are likely configured
        // The existence of the table and successful query indicates backend setup
        setHasSupabaseVAPIDKeys(true);
      } catch (error: any) {
        console.error('Error checking VAPID configuration:', error);
        setHasSupabaseVAPIDKeys(false);
      }
    };
    
    if (user?.id) {
      checkSupabaseVAPIDKeys();
      fetchRegisteredDevices(); // Also fetch devices on mount
    }
  }, [user?.id]);

  const fetchRegisteredDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const { data: devices, error } = await supabase
        .from('push_subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching devices:', error);
        toast({
          title: "Error",
          description: "Failed to fetch registered devices.",
          variant: "destructive",
        });
        return;
      }

      setRegisteredDevices(devices || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast({
        title: "Error", 
        description: "Failed to fetch registered devices.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const sendPushToSelectedDevices = async () => {
    if (selectedDevices.size === 0) {
      toast({
        title: "No Devices Selected",
        description: "Please select at least one device to send notifications to.",
        variant: "destructive",
      });
      return;
    }

    setIsSendingToSelected(true);
    let diagnostics: string[] = [];
    diagnostics.push(`🚀 Sending push notifications to ${selectedDevices.size} selected device(s)...`);

    try {
      const selectedDeviceData = registeredDevices.filter(device => 
        selectedDevices.has(device.id)
      );

      for (const device of selectedDeviceData) {
        diagnostics.push(`📱 Sending to device: ${device.endpoint.substring(0, 50)}...`);
        
        try {
          const { data, error } = await supabase.functions.invoke('send-push-notification', {
            body: {
              userId: device.user_id,
              message: {
                title: 'Admin Test Notification',
                body: `Test notification sent by admin to selected device`,
                icon: '/icon-192x192.png',
                url: '/'
              }
            }
          });

          if (error) {
            diagnostics.push(`❌ Failed for device ${device.id}: ${error.message}`);
          } else {
            diagnostics.push(`✅ Successfully sent to device ${device.id}`);
          }
        } catch (deviceError: any) {
          diagnostics.push(`❌ Error for device ${device.id}: ${deviceError.message}`);
        }
      }

      setDeviceDiagnostics(diagnostics);
      toast({
        title: "Push Notifications Sent",
        description: `Attempted to send notifications to ${selectedDevices.size} device(s). Check diagnostics for details.`,
      });

    } catch (error: any) {
      diagnostics.push(`❌ Overall error: ${error.message}`);
      setDeviceDiagnostics(diagnostics);
      toast({
        title: "Error",
        description: "Failed to send push notifications.",
        variant: "destructive",
      });
    } finally {
      setIsSendingToSelected(false);
    }
  };

  const toggleDeviceSelection = (deviceId: string) => {
    const newSelection = new Set(selectedDevices);
    if (newSelection.has(deviceId)) {
      newSelection.delete(deviceId);
    } else {
      newSelection.add(deviceId);
    }
    setSelectedDevices(newSelection);
  };

  const selectAllDevices = () => {
    setSelectedDevices(new Set(registeredDevices.map(device => device.id)));
  };

  const clearDeviceSelection = () => {
    setSelectedDevices(new Set());
  };
  
  // File input refs - expanded for more icon sizes
  const icon72InputRef = useRef<HTMLInputElement>(null);
  const icon96InputRef = useRef<HTMLInputElement>(null);
  const icon120InputRef = useRef<HTMLInputElement>(null);
  const icon128InputRef = useRef<HTMLInputElement>(null);
  const icon144InputRef = useRef<HTMLInputElement>(null);
  const icon152InputRef = useRef<HTMLInputElement>(null);
  const icon180InputRef = useRef<HTMLInputElement>(null);
  const icon192InputRef = useRef<HTMLInputElement>(null);
  const icon384InputRef = useRef<HTMLInputElement>(null);
  const icon512InputRef = useRef<HTMLInputElement>(null);
  const appleTouchInputRef = useRef<HTMLInputElement>(null);

  const iconRefs = {
    icon72: icon72InputRef,
    icon96: icon96InputRef,
    icon120: icon120InputRef,
    icon128: icon128InputRef,
    icon144: icon144InputRef,
    icon152: icon152InputRef,
    icon180: icon180InputRef,
    icon192: icon192InputRef,
    icon384: icon384InputRef,
    icon512: icon512InputRef,
    appleTouchIcon: appleTouchInputRef,
  };

  if (!settings) return null;

  const pwaSettings = settings.pwa || {
    vapidKeys: {
      publicKey: '',
      privateKey: ''
    },
    customIcons: {
      icon72: '',
      icon96: '',
      icon120: '',
      icon128: '',
      icon144: '',
      icon152: '',
      icon180: '',
      icon192: '',
      icon384: '',
      icon512: '',
      appleTouchIcon: ''
    },
    manifest: {
      name: 'LamuLamu - Local Services Platform',
      shortName: 'LamuLamu',
      description: 'Find and book local services in your area',
      themeColor: '#91DA73',
      backgroundColor: '#ffffff',
      display: 'standalone',
      orientation: 'portrait'
    }
  };

  // Ensure customIcons exists even if not in database
  if (!pwaSettings.customIcons) {
    pwaSettings.customIcons = {
      icon72: '',
      icon96: '',
      icon120: '',
      icon128: '',
      icon144: '',
      icon152: '',
      icon180: '',
      icon192: '',
      icon384: '',
      icon512: '',
      appleTouchIcon: ''
    };
  }

  // Migrate icons from manifest.icons to customIcons if needed
  if (pwaSettings.manifest?.icons) {
    if (pwaSettings.manifest.icons.icon192 && !pwaSettings.customIcons.icon192) {
      pwaSettings.customIcons.icon192 = pwaSettings.manifest.icons.icon192;
      updateSettings(['pwa', 'customIcons', 'icon192'], pwaSettings.manifest.icons.icon192);
    }
    if (pwaSettings.manifest.icons.icon512 && !pwaSettings.customIcons.icon512) {
      pwaSettings.customIcons.icon512 = pwaSettings.manifest.icons.icon512;
      updateSettings(['pwa', 'customIcons', 'icon512'], pwaSettings.manifest.icons.icon512);
    }
    if (pwaSettings.manifest.icons.appleTouchIcon && !pwaSettings.customIcons.appleTouchIcon) {
      pwaSettings.customIcons.appleTouchIcon = pwaSettings.manifest.icons.appleTouchIcon;
      updateSettings(['pwa', 'customIcons', 'appleTouchIcon'], pwaSettings.manifest.icons.appleTouchIcon);
    }
  }

  // Check which required PWA icons are available (including iPhone-specific sizes)
  const requiredIcons = ['72', '96', '120', '128', '144', '152', '180', '192', '384', '512'];
  const iPhoneIcons = ['120', '180']; // Critical for iPhone "Add to Home Screen"
  const missingIcons = requiredIcons.filter(size => !pwaSettings.customIcons[`icon${size}`]);
  const missingCriticalIcons = iPhoneIcons.filter(size => !pwaSettings.customIcons[`icon${size}`]);
  const availableIcons = requiredIcons.filter(size => pwaSettings.customIcons[`icon${size}`]);

  const pushSettings = settings.push || {
    enabled: false,
    notifications: {
      signups: false,
      bookings: false
    }
  };

  const generateVAPIDKeys = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to generate VAPID keys.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingKeys(true);
    try {
      // Generate VAPID keys using Web Crypto API
      const keyPair = await crypto.subtle.generateKey(
        {
          name: 'ECDSA',
          namedCurve: 'P-256'
        },
        true,
        ['sign', 'verify']
      );

      const publicKeyBuffer = await crypto.subtle.exportKey('raw', keyPair.publicKey);
      const privateKeyBuffer = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

      const publicKey = btoa(String.fromCharCode(...new Uint8Array(publicKeyBuffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

      const privateKey = btoa(String.fromCharCode(...new Uint8Array(privateKeyBuffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

      // Store both keys in settings for display (to be manually stored in secrets)
      updateSettings(['pwa', 'vapidKeys', 'publicKey'], publicKey);
      updateSettings(['pwa', 'vapidKeys', 'privateKey'], privateKey);

      toast({
        title: "VAPID Keys Generated",
        description: "Keys generated successfully. Please copy both keys and store them manually in Supabase secrets (VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY).",
      });
    } catch (error) {
      console.error('Error generating VAPID keys:', error);
      toast({
        title: "Error",
        description: "Failed to generate VAPID keys.",
        variant: "destructive",
      });
     }
     setIsGeneratingKeys(false);
   };

   const checkVAPIDStatus = () => {
     const hasPublicKey = pwaSettings.vapidKeys.publicKey && pwaSettings.vapidKeys.publicKey.length > 0;
     const hasPrivateKey = pwaSettings.vapidKeys.privateKey && pwaSettings.vapidKeys.privateKey.length > 0;
     const hasLocalKeys = hasPublicKey && hasPrivateKey;
     const hasSupabaseKeys = hasSupabaseVAPIDKeys === true;
     
     return {
       publicKeyConfigured: hasPublicKey || hasSupabaseKeys,
       privateKeyConfigured: hasPrivateKey || hasSupabaseKeys,
       isConfigured: hasLocalKeys || hasSupabaseKeys,
       hasSupabaseKeys,
       hasLocalKeys,
     };
   };

   const clearVAPIDKeys = () => {
     updateSettings(['pwa', 'vapidKeys', 'publicKey'], '');
     updateSettings(['pwa', 'vapidKeys', 'privateKey'], '');
     toast({
       title: "Keys Cleared",
       description: "VAPID keys have been cleared. Generate new keys when ready.",
     });
   };

   const vapidStatus = checkVAPIDStatus();

   const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast({
        title: "Copied!",
        description: `${field} copied to clipboard.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard.",
        variant: "destructive",
      });
    }
  };

  const sendTestPushNotification = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to send test notifications.",
        variant: "destructive",
      });
      return;
    }

    // Check if VAPID keys are configured before attempting to send
    if (!vapidStatus.isConfigured) {
      toast({
        title: "VAPID Keys Required",
        description: "Please generate VAPID keys before sending push notifications.",
        variant: "destructive",
      });
      return;
    }

    setIsSendingTestPush(true);
    let diagnostics: string[] = [];
    
    try {
      diagnostics.push("🚀 Starting comprehensive push notification test...");
      diagnostics.push(`✅ User authenticated: ${user.id}`);
      
      // Check browser push support
      diagnostics.push("🔍 Checking browser push support...");
      if (!('serviceWorker' in navigator)) {
        diagnostics.push("❌ Service Workers not supported");
      } else if (!('PushManager' in window)) {
        diagnostics.push("❌ Push API not supported");
      } else {
        diagnostics.push("✅ Browser supports push notifications");
      }
      
      // Check service worker registration and active subscription
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          diagnostics.push("✅ Service worker is registered");
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            diagnostics.push("✅ Browser has active push subscription");
            diagnostics.push(`   Endpoint: ${subscription.endpoint.substring(0, 60)}...`);
            const keys = subscription.getKey ? {
              p256dh: subscription.getKey('p256dh'),
              auth: subscription.getKey('auth')
            } : null;
            diagnostics.push(`   Has encryption keys: ${keys ? 'Yes' : 'No'}`);
          } else {
            diagnostics.push("❌ No active browser push subscription");
            diagnostics.push("💡 Try re-registering your device first");
          }
        } else {
          diagnostics.push("❌ No service worker registration found");
        }
      } catch (swError) {
        diagnostics.push(`⚠️ Service worker check failed: ${swError.message}`);
      }
      
      // Check VAPID keys in settings
      diagnostics.push("🔑 Verifying VAPID keys...");
      const vapidKeys = settings?.pwa?.vapidKeys;
      if (!vapidKeys?.publicKey || !vapidKeys?.privateKey) {
        diagnostics.push("❌ VAPID keys not configured in admin settings");
        diagnostics.push("💡 This should not happen - keys were verified earlier");
      } else {
        diagnostics.push("✅ VAPID keys are configured");
        diagnostics.push(`   Public key length: ${vapidKeys.publicKey.length} chars`);
        diagnostics.push(`   Private key length: ${vapidKeys.privateKey.length} chars`);
      }
      
      // Check if push subscriptions exist in database
      diagnostics.push("🗄️ Checking push subscriptions in database...");
      const { data: subscriptions, error: dbError } = await supabase
        .from('push_subscriptions')
        .select('id, endpoint, auth_key, p256dh_key, created_at, updated_at')
        .eq('user_id', user.id);
      
      if (dbError) {
        diagnostics.push(`❌ Database error: ${dbError.message}`);
        throw new Error(`Failed to check subscriptions: ${dbError.message}`);
      }
      
      if (!subscriptions || subscriptions.length === 0) {
        diagnostics.push("❌ No push subscriptions found in database");
        diagnostics.push("💡 This means the device registration didn't save to database");
        diagnostics.push("💡 Try refreshing and re-testing device registration");
        
        toast({
          title: "No Database Subscriptions",
          description: (
            <div className="space-y-1 text-xs max-h-32 overflow-y-auto">
              {diagnostics.map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </div>
          ),
          variant: "destructive",
          duration: 8000,
        });
        return;
      }
      
      diagnostics.push(`✅ Found ${subscriptions.length} subscription(s) in database`);
      subscriptions.forEach((sub, index) => {
        diagnostics.push(`   ${index + 1}. Endpoint: ${sub.endpoint.substring(0, 50)}...`);
        diagnostics.push(`      Created: ${new Date(sub.created_at).toLocaleString()}`);
        diagnostics.push(`      Updated: ${new Date(sub.updated_at || sub.created_at).toLocaleString()}`);
        diagnostics.push(`      Has auth key: ${sub.auth_key ? 'Yes' : 'No'}`);
        diagnostics.push(`      Has p256dh key: ${sub.p256dh_key ? 'Yes' : 'No'}`);
      });
      
      // Call the edge function with enhanced logging
      diagnostics.push("📡 Calling push notification edge function...");
      console.log('🔔 Starting edge function call with user:', user.id);
      console.log('🔔 Subscriptions found in database:', subscriptions?.length);
      subscriptions?.forEach((sub, i) => {
        console.log(`🔔 Subscription ${i + 1}:`);
        console.log(`   Endpoint: ${sub.endpoint}`);
        console.log(`   Auth key present: ${!!sub.auth_key}`);
        console.log(`   P256dh key present: ${!!sub.p256dh_key}`);
        console.log(`   User ID: ${sub.user_id}`);
      });
      
      const startTime = Date.now();
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          userId: user.id,
          message: {
            title: 'Test Push Notification',
            body: 'This is a test notification from your LamuLamu admin panel!',
            icon: '/icon-192x192.png',
            url: '/'
          }
        }
      });
      
      const duration = Date.now() - startTime;
      diagnostics.push(`⏱️ Edge function completed in ${duration}ms`);
      console.log('🔔 Edge function response data:', data);
      console.log('🔔 Edge function response error:', error);

      if (error) {
        diagnostics.push(`❌ Edge function error: ${error.message}`);
        console.error('🔔 Detailed edge function error:', error);
        
        // Categorize errors for better guidance
        if (error.message.includes('not registered') || error.message.includes('device not registered')) {
          diagnostics.push("💡 'Not registered' suggests RLS policy or database access issue");
          diagnostics.push("💡 Edge function may not be able to read push_subscriptions table");
          console.error('🔔 RLS Policy Issue: Edge function cannot access push_subscriptions');
        } else if (error.message.includes('permission') || error.message.includes('403')) {
          diagnostics.push("💡 This is likely a VAPID key or authentication issue");
          diagnostics.push("💡 Check VAPID keys format and FCM authentication headers");
          console.error('🔔 VAPID Authentication Issue: Check keys and headers format');
        } else if (error.message.includes('endpoint')) {
          diagnostics.push("💡 This suggests a push service connectivity issue");
          console.error('🔔 Push Service Connectivity Issue');
        } else if (error.message.includes('crypto-key')) {
          diagnostics.push("💡 This is a VAPID header formatting issue in edge function");
          diagnostics.push("💡 FCM requires both Authorization and Crypto-Key headers");
          console.error('🔔 VAPID Header Format Issue: Missing or incorrect crypto-key header');
        }
        
        diagnostics.push(`🔍 Full error details: ${JSON.stringify(error, null, 2)}`);
        console.error('🔔 Complete error object:', JSON.stringify(error, null, 2));
        throw error;
      }

      diagnostics.push("✅ Edge function executed successfully");
      diagnostics.push(`📊 Result: ${JSON.stringify(data, null, 2)}`);

      if (data.totalSent === 0) {
        diagnostics.push("⚠️ Edge function succeeded but no notifications were delivered");
        diagnostics.push("💡 Check push service logs or endpoint validity");
        toast({
          title: "Push Service Issue",
          description: (
            <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
              {diagnostics.map((item, index) => (
                <div key={index} className="font-mono">{item}</div>
              ))}
            </div>
          ),
          variant: "destructive",
          duration: 12000,
        });
      } else {
        diagnostics.push(`🎉 Successfully sent ${data.totalSent} notification(s)`);
        toast({
          title: "Test Notification Sent!",
          description: (
            <div className="space-y-2">
              <div>Notification sent to {data.totalSent} subscription(s).</div>
              <details className="text-xs">
                <summary>View diagnostics</summary>
                <div className="space-y-1 mt-2 max-h-32 overflow-y-auto">
                  {diagnostics.map((item, index) => (
                    <div key={index} className="font-mono">{item}</div>
                  ))}
                </div>
              </details>
            </div>
          ),
          duration: 8000,
        });
      }
    } catch (error) {
      console.error('Error sending test push notification:', error);
      diagnostics.push(`❌ Push notification test failed: ${error.message}`);
      
      // Add more context about the error
      if (error.name) {
        diagnostics.push(`🏷️ Error type: ${error.name}`);
      }
      
      // Provide troubleshooting guidance based on error
      if (error.message.includes('Failed to fetch')) {
        diagnostics.push("🌐 Network error - check internet connection");
      } else if (error.message.includes('not authenticated')) {
        diagnostics.push("🔐 Authentication error - try refreshing and logging in again");
      } else if (error.message.includes('subscriptions')) {
        diagnostics.push("📱 Device registration issue - try registering device again");
      } else if (error.message.includes('VAPID')) {
        diagnostics.push("🔑 VAPID key issue - check admin settings configuration");
      }
      
      diagnostics.push(`🔍 Full error object: ${JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}`);
      diagnostics.push("🛠️ Troubleshooting: Check browser console and edge function logs");
      
      toast({
        title: "Push Notification Test Failed",
        description: (
          <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
            {diagnostics.map((item, index) => (
              <div key={index} className="font-mono">{item}</div>
            ))}
          </div>
        ),
        variant: "destructive",
        duration: 15000,
      });
    } finally {
      setIsSendingTestPush(false);
    }
  };

  const checkDeviceRegistration = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to check device registration.",
        variant: "destructive",
      });
      return;
    }

    setIsCheckingRegistration(true);
    let diagnostics: string[] = [];

    try {
      // 1. Check browser support
      if (!('Notification' in window)) {
        diagnostics.push("❌ Browser does not support notifications");
      } else {
        diagnostics.push("✅ Browser supports notifications");
      }

      if (!('serviceWorker' in navigator)) {
        diagnostics.push("❌ Service Worker not supported");
      } else {
        diagnostics.push("✅ Service Worker supported");
      }

      // 2. Check notification permission
      const permission = Notification.permission;
      diagnostics.push(`🔒 Notification permission: ${permission}`);

      // 3. Check service worker registration
      try {
        const registration = await navigator.serviceWorker.ready;
        diagnostics.push("✅ Service Worker registered");
        
        // 4. Check for existing push subscription
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          diagnostics.push("✅ Push subscription exists in browser");
          
          // 5. Check if subscription is stored in database
          const { data: dbSubscriptions, error: dbError } = await supabase
            .from('push_subscriptions')
            .select('id, endpoint, created_at')
            .eq('user_id', user.id);
          
          if (dbError) {
            diagnostics.push(`❌ Database error: ${dbError.message}`);
          } else if (dbSubscriptions && dbSubscriptions.length > 0) {
            diagnostics.push(`✅ Found ${dbSubscriptions.length} subscription(s) in database`);
            
            // Check if current browser subscription matches database
            const matchingSubscription = dbSubscriptions.find(sub => sub.endpoint === subscription.endpoint);
            if (matchingSubscription) {
              diagnostics.push("✅ Current device subscription found in database");
            } else {
              diagnostics.push("⚠️ Current device subscription not found in database (may need re-registration)");
            }
          } else {
            diagnostics.push("❌ No subscriptions found in database");
          }
        } else {
          diagnostics.push("❌ No push subscription found in browser");
        }
      } catch (swError) {
        diagnostics.push(`❌ Service Worker error: ${swError.message}`);
      }

      // 6. Check VAPID configuration
      if (hasSupabaseVAPIDKeys) {
        diagnostics.push("✅ VAPID keys configured in Supabase");
      } else {
        diagnostics.push("❌ VAPID keys not configured in Supabase");
      }

      // Show results
      toast({
        title: "Device Registration Check",
        description: (
          <div className="space-y-1 text-xs max-h-64 overflow-y-auto">
            {diagnostics.map((item, index) => (
              <div key={index}>{item}</div>
            ))}
          </div>
        ),
        duration: 10000,
      });

    } catch (error: any) {
      console.error('Error checking device registration:', error);
      toast({
        title: "Check Failed",
        description: `Failed to check device registration: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsCheckingRegistration(false);
    }
  };

  const testSigningAndEncryption = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to test signing and encryption.",
        variant: "destructive",
      });
      return;
    }

    setIsTestingSigning(true);
    let diagnostics: string[] = [];

    try {
      diagnostics.push("🔧 Starting VAPID signing and encryption test...");

      // Check VAPID keys using the same logic as checkVAPIDStatus
      const vapidStatus = checkVAPIDStatus();
      const publicKey = pwaSettings.vapidKeys.publicKey;
      const privateKey = pwaSettings.vapidKeys.privateKey;

      diagnostics.push(`📊 VAPID Status Check:`);
      diagnostics.push(`   • Local keys configured: ${vapidStatus.hasLocalKeys}`);
      diagnostics.push(`   • Supabase keys configured: ${vapidStatus.hasSupabaseKeys}`);  
      diagnostics.push(`   • Overall configured: ${vapidStatus.isConfigured}`);

      if (!vapidStatus.isConfigured) {
        diagnostics.push("❌ No VAPID keys found (neither local nor Supabase)");
        throw new Error("VAPID keys not configured - please generate keys or configure them in Supabase");
      }

      if (vapidStatus.hasLocalKeys) {
        diagnostics.push("✅ Local VAPID keys found");
        diagnostics.push(`📝 Public key length: ${publicKey?.length || 0} chars`);
        diagnostics.push(`📝 Private key length: ${privateKey?.length || 0} chars`);
      } else if (vapidStatus.hasSupabaseKeys) {
        diagnostics.push("✅ Supabase VAPID keys configured");
        diagnostics.push("📝 Keys are stored securely in Supabase secrets");
      }

      // Test message payload
      const testMessage = {
        title: "Test Message",
        body: "Testing VAPID signing and encryption",
        icon: "/icon-192x192.png",
        badge: "/icon-192x192.png"
      };

      diagnostics.push("📦 Test payload created");
      diagnostics.push(`📝 Payload size: ${JSON.stringify(testMessage).length} bytes`);

      // Test JWT creation (VAPID signing simulation)
      try {
        const header = {
          typ: "JWT",
          alg: "ES256"
        };

        const payload = {
          aud: "https://fcm.googleapis.com",
          exp: Math.floor(Date.now() / 1000) + 43200, // 12 hours
          sub: "mailto:test@example.com"
        };

        const encodedHeader = btoa(JSON.stringify(header)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
        const encodedPayload = btoa(JSON.stringify(payload)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

        diagnostics.push("✅ JWT header encoded successfully");
        diagnostics.push("✅ JWT payload encoded successfully");
        diagnostics.push(`📝 JWT header: ${encodedHeader.substring(0, 20)}...`);
        diagnostics.push(`📝 JWT payload: ${encodedPayload.substring(0, 20)}...`);

        // Test private key format validation
        if (privateKey.includes('BEGIN PRIVATE KEY') || privateKey.includes('BEGIN EC PRIVATE KEY')) {
          diagnostics.push("✅ Private key appears to be in PEM format");
        } else {
          diagnostics.push("⚠️ Private key format unclear - may need conversion");
        }

        // Test public key format validation
        if (publicKey.length === 88) {
          diagnostics.push("✅ Public key length matches VAPID standard (88 chars)");
        } else {
          diagnostics.push(`⚠️ Public key length (${publicKey.length}) may not match VAPID standard`);
        }

        diagnostics.push("🎯 VAPID signing test completed successfully");

      } catch (signingError: any) {
        diagnostics.push(`❌ JWT signing test failed: ${signingError.message}`);
        throw signingError;
      }

      // Test encryption simulation
      try {
        const payload = JSON.stringify(testMessage);
        const payloadBuffer = new TextEncoder().encode(payload);
        
        diagnostics.push("✅ Payload encoded to buffer successfully");
        diagnostics.push(`📝 Buffer length: ${payloadBuffer.length} bytes`);

        // Generate random salt and key for encryption test
        const salt = crypto.getRandomValues(new Uint8Array(16));
        const keyMaterial = crypto.getRandomValues(new Uint8Array(16));

        diagnostics.push("✅ Encryption materials generated");
        diagnostics.push(`📝 Salt length: ${salt.length} bytes`);
        diagnostics.push(`📝 Key material length: ${keyMaterial.length} bytes`);

        diagnostics.push("🔐 Encryption test completed successfully");

      } catch (encryptionError: any) {
        diagnostics.push(`❌ Encryption test failed: ${encryptionError.message}`);
        throw encryptionError;
      }

      // Success summary
      diagnostics.push("🎉 All signing and encryption tests passed!");
      diagnostics.push("✅ Ready for push notification delivery");

      toast({
        title: "Signing & Encryption Test Passed",
        description: (
          <div className="space-y-2">
            <p className="font-medium">All tests completed successfully:</p>
            <div className="text-sm space-y-1 max-h-64 overflow-y-auto">
              {diagnostics.map((diagnostic, index) => (
                <div key={index} className="font-mono text-xs p-1 bg-muted rounded">
                  {diagnostic}
                </div>
              ))}
            </div>
          </div>
        ),
        duration: 10000,
      });

    } catch (error: any) {
      console.error('Error testing signing and encryption:', error);
      diagnostics.push(`❌ Test failed: ${error.message}`);
      
      toast({
        title: "Signing & Encryption Test Failed",
        description: (
          <div className="space-y-2">
            <p className="font-medium text-destructive">Test failed with errors:</p>
            <div className="text-sm space-y-1 max-h-64 overflow-y-auto">
              {diagnostics.map((diagnostic, index) => (
                <div key={index} className="font-mono text-xs p-1 bg-muted rounded">
                  {diagnostic}
                </div>
              ))}
            </div>
          </div>
        ),
        variant: "destructive",
        duration: 10000,
      });
    } finally {
      setIsTestingSigning(false);
    }
  };

  const sendAdminBroadcast = async () => {
    if (!user) return;
    
    try {
      setIsSendingTestPush(true);
      
      const { data, error } = await supabase.functions.invoke('send-admin-broadcast', {
        body: {
          adminUserId: user.id,
          message: {
            title: 'Admin Broadcast Test',
            body: 'This notification was sent to all your logged-in devices!',
            icon: '/pwa-icons/icon-192x192.png',
            url: '/admin/settings'
          }
        }
      });

      if (error) {
        console.error('Admin broadcast error:', error);
        toast({
          title: "Broadcast failed",
          description: error.message || "Could not send broadcast notification",
          variant: "destructive",
        });
        return;
      }

      console.log('Admin broadcast result:', data);
      
      if (data?.success) {
        toast({
          title: "Broadcast sent",
          description: `Notification sent to ${data.sentCount} of ${data.totalSubscriptions} devices`,
        });
      } else {
        toast({
          title: "No devices found", 
          description: data?.message || "No push subscriptions found for your account",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Admin broadcast failed:', error);
      toast({
        title: "Broadcast failed",
        description: "Could not send broadcast notification",
        variant: "destructive",
      });
    } finally {
      setIsSendingTestPush(false);
    }
  };

  const enablePushNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast({
        title: "Push Notifications Not Supported",
        description: "Your browser doesn't support push notifications.",
        variant: "destructive",
      });
      return false;
    }

    try {
      // Check current permission
      let permission = Notification.permission;
      
      // Request permission if not already granted
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }
      
      if (permission !== 'granted') {
        toast({
          title: "Permission Denied",
          description: "Push notification permission was denied. Please enable it in your browser settings.",
          variant: "destructive",
        });
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Get or create subscription
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Get VAPID public key from backend
        const { data: vapidKeyData, error: keyError } = await supabase.functions.invoke('get-vapid-key');
        
        if (keyError || !vapidKeyData?.publicKey) {
          throw new Error('Failed to get VAPID key from server');
        }

        // Convert URL-safe base64 to Uint8Array for applicationServerKey
        const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
          const padding = '='.repeat((4 - base64String.length % 4) % 4);
          const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');
          const rawData = window.atob(base64);
          const outputArray = new Uint8Array(rawData.length);
          for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
          }
          return outputArray;
        };
        
        // Create new subscription
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKeyData.publicKey)
        });
      }

      // Always ensure subscription is stored in database (even if browser already had one)
      if (!user?.id) {
        throw new Error('Authentication required. Please log in and try again.');
      }

      const { error: storeError } = await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: subscription.endpoint,
          p256dh_key: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh') || new ArrayBuffer(0)))),
          auth_key: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth') || new ArrayBuffer(0)))),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,endpoint'
        });

      if (storeError) {
        console.error('Failed to store subscription in database:', storeError);
        throw new Error(`Database storage failed: ${storeError.message}`);
      }

      toast({
        title: "Push Notifications Ready!",
        description: "Device is now registered for push notifications. You can test it below.",
      });
      
      return true;
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      toast({
        title: "Setup Failed",
        description: `Failed to enable push notifications: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
      return false;
    }
  };
  const handleIconUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    targetSize: number,
    iconType: keyof typeof pwaSettings.customIcons
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate icon before upload
    const validationResult = await validateIcon(file, { width: targetSize, height: targetSize });
    
    if (!validationResult.isValid) {
      return; // Validation hook already shows error toast
    }
    
    const result = await uploadIcon({ 
      file, 
      targetSize: { width: targetSize, height: targetSize }, 
      iconType 
    });
    
    if (result.success) {
      updateSettings(['pwa', 'customIcons', iconType], result.url);
      
      // Refresh PWA assets to use new icon
      setTimeout(() => {
        refreshPWAAssets();
      }, 1000);
      
      // If this is the 512x512 icon and there are missing icons, suggest generating them
      if (targetSize === 512 && missingIcons.filter(s => s !== '512').length > 0) {
        toast({
          title: "Icon uploaded successfully",
          description: `${iconType} has been uploaded. You can now generate ${missingIcons.filter(s => s !== '512').length} missing icon sizes automatically.`,
        });
      } else {
        toast({
          title: "Icon uploaded successfully",
          description: `${iconType} has been uploaded and PWA assets refreshed.`,
        });
      }
    }
  };

  const triggerFileInput = (iconKey: keyof typeof iconRefs) => {
    iconRefs[iconKey].current?.click();
  };

  const handleGenerateMissingIcons = async () => {
    // Check if we have a 512x512 icon to use as source
    const sourceIconUrl = pwaSettings.customIcons.icon512;
    
    if (!sourceIconUrl) {
      toast({
        title: "No source icon found",
        description: "Please upload a 512x512 icon first to generate other sizes.",
        variant: "destructive",
      });
      return;
    }

    // Determine which sizes are missing
    const sizesToGenerate = missingIcons.map(size => parseInt(size));
    
    if (sizesToGenerate.length === 0) {
      toast({
        title: "No icons to generate",
        description: "All required icon sizes are already uploaded.",
      });
      return;
    }

    try {
      const result = await generateMissingIcons({
        sourceImageUrl: sourceIconUrl,
        sizes: sizesToGenerate,
        onProgress: (current, total) => {
          setGenerationProgress({ current, total });
        }
      });

      // Update settings with successful results
      result.results.forEach((iconResult) => {
        if (iconResult.success) {
          const iconKey = `icon${iconResult.size}` as keyof typeof pwaSettings.customIcons;
          updateSettings(['pwa', 'customIcons', iconKey], iconResult.url);
        }
      });

      // Clear progress
      setGenerationProgress(null);

      // Refresh PWA assets after a short delay
      if (result.successCount > 0) {
        setTimeout(() => {
          refreshPWAAssets();
        }, 1000);
      }

    } catch (error) {
      console.error('Error generating icons:', error);
      setGenerationProgress(null);
    }
  };

  const handleTestIconGeneration = async () => {
    const testSizes = [72, 96, 128, 192];
    const sourceUrl = pwaSettings.customIcons.icon512 || '/placeholder.svg';
    
    try {
      toast({
        title: "Testing icon generation",
        description: `Generating test icons from ${pwaSettings.customIcons.icon512 ? '512x512 source' : 'placeholder image'}...`,
      });

      const result = await generateMissingIcons({
        sourceImageUrl: sourceUrl,
        sizes: testSizes,
        onProgress: (current, total) => {
          setGenerationProgress({ current, total });
        }
      });

      toast({
        title: "Test completed",
        description: `Generated ${result.successCount}/${result.totalCount} test icons. Check browser console for details.`,
        variant: result.allSuccessful ? "default" : "destructive",
      });

      console.log('Test generation results:', result);
      console.log('Generated icon URLs:', result.results);
    } catch (error) {
      console.error('Test generation failed:', error);
      toast({
        title: "Test failed",
        description: "Icon generation test encountered an error. Check console for details.",
        variant: "destructive",
      });
    } finally {
      setGenerationProgress(null);
    }
  };

  const clearPushSubscription = async () => {
    try {
      // Clear browser subscription
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      // Clear database subscription directly
      if (user?.id) {
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id', user.id);
      }

      toast({
        title: "Push Subscription Cleared",
        description: "Push notifications have been disabled. You can re-enable them with the button above.",
      });
    } catch (error) {
      console.error('Error clearing push subscription:', error);
      toast({
        title: "Clear Failed", 
        description: "Failed to clear push subscription. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getPushSubscriptionStatus = async () => {
    try {
      // Check browser permission
      const permission = Notification.permission;
      
      // Check browser subscription
      let hasSubscription = false;
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        hasSubscription = !!subscription;
      }

      // Check database subscription
      const { data: dbSubscriptions } = await supabase
        .from('push_subscriptions')
        .select('id')
        .eq('user_id', user?.id);

      return {
        permission,
        hasSubscription,
        dbSubscriptions: dbSubscriptions?.length || 0
      };
    } catch (error) {
      console.error('Error getting push subscription status:', error);
      return {
        permission: 'default',
        hasSubscription: false,
        dbSubscriptions: 0
      };
    }
  };

  const runPWADiagnostics = async () => {
    try {
      // Fetch and analyze dynamic manifest
      const manifestResponse = await fetch('https://ginefbvgxcobyuocxerb.supabase.co/functions/v1/dynamic-manifest');
      const manifest = await manifestResponse.json();
      setManifestData(manifest);

      // Check service worker
      const swData = {
        supported: 'serviceWorker' in navigator,
        registered: null as any,
        active: null as any,
        waiting: null as any
      };

      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          swData.registered = {
            scope: registration.scope,
            updateViaCache: registration.updateViaCache
          };
          swData.active = registration.active ? {
            state: registration.active.state,
            scriptURL: registration.active.scriptURL
          } : null;
          swData.waiting = registration.waiting ? {
            state: registration.waiting.state,
            scriptURL: registration.waiting.scriptURL
          } : null;
        }
      }
      setSWStatus(swData);

      // Gather debug info
      const debug = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        standalone: (window.navigator as any).standalone,
        displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 
                    window.matchMedia('(display-mode: fullscreen)').matches ? 'fullscreen' :
                    window.matchMedia('(display-mode: minimal-ui)').matches ? 'minimal-ui' : 'browser',
        beforeInstallPrompt: (window as any).beforeInstallPromptFired || false,
        isSecureContext: window.isSecureContext,
        protocol: window.location.protocol,
        host: window.location.host,
        manifestErrors: [] as string[]
      };

      // Validate manifest
      if (manifest) {
        if (!manifest.name || manifest.name.length === 0) {
          debug.manifestErrors.push('Missing required "name" field');
        }
        if (!manifest.icons || manifest.icons.length === 0) {
          debug.manifestErrors.push('Missing required "icons" field');
        }
        if (!manifest.start_url) {
          debug.manifestErrors.push('Missing required "start_url" field');
        }
        if (manifest.start_url && !manifest.start_url.startsWith(window.location.origin) && !manifest.start_url.startsWith('/')) {
          debug.manifestErrors.push(`Invalid start_url: ${manifest.start_url}`);
        }
        if (manifest.scope && !manifest.scope.startsWith(window.location.origin) && !manifest.scope.startsWith('/')) {
          debug.manifestErrors.push(`Invalid scope: ${manifest.scope}`);
        }
      }

      setDebugInfo(debug);

      toast({
        title: "PWA Diagnostics Complete",
        description: "Debug information has been gathered.",
      });
    } catch (error) {
      console.error('PWA diagnostics error:', error);
      toast({
        title: "Diagnostics Error",
        description: "Failed to gather PWA debug information.",
        variant: "destructive",
      });
    }
  };

  const debugInstall = async () => {
    const result = await triggerInstall();
    toast({
      title: result ? "Install Triggered" : "Install Failed",
      description: result ? "PWA install prompt was triggered" : "Failed to trigger install prompt",
      variant: result ? "default" : "destructive",
    });
  };

  return (
    <div className="space-y-6">
      {/* Device Management Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Registered Devices ({registeredDevices.length})
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRegisteredDevices}
              disabled={isLoadingDevices}
              className="ml-auto"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingDevices ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardTitle>
          <CardDescription>
            Manage and test push notifications for all registered devices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {registeredDevices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Smartphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No devices registered yet</p>
              <p className="text-sm">Enable push notifications on devices to see them here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selection Controls */}
              <div className="flex flex-wrap gap-2 items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllDevices}
                    disabled={selectedDevices.size === registeredDevices.length}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearDeviceSelection}
                    disabled={selectedDevices.size === 0}
                  >
                    Clear Selection
                  </Button>
                </div>
                <span className="text-sm text-muted-foreground">
                  {selectedDevices.size} of {registeredDevices.length} selected
                </span>
              </div>

              {/* Device List */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {registeredDevices.map((device) => {
                  const isSelected = selectedDevices.has(device.id);
                  const deviceType = device.endpoint.includes('fcm.googleapis.com') 
                    ? 'Android/Chrome' 
                    : device.endpoint.includes('web.push.apple.com')
                    ? 'iOS/Safari'
                    : device.endpoint.includes('wns.notify.windows.com')
                    ? 'Windows'
                    : 'Unknown';
                  
                  return (
                    <div
                      key={device.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-primary/10 border-primary' 
                          : 'bg-card hover:bg-muted/50'
                      }`}
                      onClick={() => toggleDeviceSelection(device.id)}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                      }`}>
                        {isSelected && <CheckCircle2 className="w-3 h-3 text-primary-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{deviceType}</span>
                          <span className="text-xs bg-muted px-2 py-1 rounded">
                            {new Date(device.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {device.endpoint.substring(0, 60)}...
                        </p>
                        {device.user_agent && (
                          <p className="text-xs text-muted-foreground/70 truncate">
                            {device.user_agent}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={sendPushToSelectedDevices}
                  disabled={selectedDevices.size === 0 || isSendingToSelected}
                  className="flex items-center gap-2"
                >
                  <Bell className="h-4 w-4" />
                  {isSendingToSelected 
                    ? `Sending to ${selectedDevices.size} device(s)...` 
                    : `Send Test Push (${selectedDevices.size})`
                  }
                </Button>
              </div>

              {/* Diagnostics */}
              {deviceDiagnostics.length > 0 && (
                <div className="mt-4 space-y-2">
                  <Label className="flex items-center gap-2">
                    <Bug className="h-4 w-4" />
                    Push Notification Diagnostics
                  </Label>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <div className="space-y-1 text-xs font-mono max-h-32 overflow-y-auto">
                      {deviceDiagnostics.map((item, index) => (
                        <div 
                          key={index}
                          className={`${
                            item.includes('✅') ? 'text-green-600 dark:text-green-400' :
                            item.includes('❌') || item.includes('Error') ? 'text-red-600 dark:text-red-400' :
                            item.includes('⚠️') ? 'text-yellow-600 dark:text-yellow-400' :
                            item.includes('💡') ? 'text-blue-600 dark:text-blue-400' :
                            'text-muted-foreground'
                          }`}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service Worker Debug Section */} 
      <ServiceWorkerDebug />

      {/* VAPID Keys Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            VAPID Keys for Push Notifications
          </CardTitle>
          <CardDescription>
            Generate VAPID keys required for push notifications. These keys authenticate your server with push services.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status indicator */}
          <div className="flex items-center gap-2 p-3 rounded-lg border">
            {vapidStatus.isConfigured ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-700">VAPID Keys Configured</p>
                  <p className="text-sm text-green-600">
                    {vapidStatus.hasSupabaseKeys ? 
                      "Keys found in Supabase secrets - Push notifications ready" :
                      "Local keys configured - Push notifications ready"
                    }
                  </p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-700">VAPID Keys Required</p>
                  <p className="text-sm text-amber-600">
                    {hasSupabaseVAPIDKeys === null ?
                      "Checking Supabase secrets..." :
                      "Generate keys or configure in Supabase secrets"
                    }
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="publicKey">Public Key</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="publicKey"
                  value={pwaSettings.vapidKeys.publicKey || 'Not generated'}
                  readOnly
                  placeholder="Generate keys to see public key..."
                  className="font-mono text-sm bg-muted"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(pwaSettings.vapidKeys.publicKey, 'Public Key')}
                  disabled={!pwaSettings.vapidKeys.publicKey}
                >
                  {copiedField === 'Public Key' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Store this as VAPID_PUBLIC_KEY in Supabase secrets
              </p>
            </div>

            <div>
              <Label htmlFor="privateKey">Private Key</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="privateKey"
                  type="password"
                  value={pwaSettings.vapidKeys.privateKey || 'Not generated'}
                  readOnly
                  placeholder="Generate keys to see private key..."
                  className="font-mono text-sm bg-muted"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(pwaSettings.vapidKeys.privateKey, 'Private Key')}
                  disabled={!pwaSettings.vapidKeys.privateKey}
                >
                  {copiedField === 'Private Key' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Store this as VAPID_PRIVATE_KEY in Supabase secrets
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button 
                onClick={generateVAPIDKeys} 
                disabled={isGeneratingKeys}
                variant="default"
              >
                <Key className="h-4 w-4 mr-2" />
                {isGeneratingKeys ? "Generating..." : "Generate Keys"}
              </Button>
              
              {vapidStatus.isConfigured && (
                <Button 
                  onClick={clearVAPIDKeys} 
                  disabled={isGeneratingKeys}
                  variant="outline"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Clear Keys
                </Button>
              )}
            </div>

            {vapidStatus.isConfigured && (
              <div className="text-xs text-muted-foreground p-2 rounded border bg-blue-50 dark:bg-blue-950/20">
                <AlertCircle className="h-3 w-3 inline mr-1" />
                After copying both keys to Supabase secrets, you can clear them from this display for security
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Admin Push Notifications
          </CardTitle>
          <CardDescription>
            Configure push notifications for admin users. Admins will receive push notifications when enabled.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="push-enabled">Enable Push Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Enable push notifications for admin users
              </p>
            </div>
            <Switch
              id="push-enabled"
              checked={pushSettings.enabled}
              onCheckedChange={(checked) => updateSettings(['push', 'enabled'], checked)}
            />
          </div>

          {pushSettings.enabled && (
            <div className="space-y-4 pt-4 border-t">
              <h4 className="text-sm font-medium">Notification Events</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="push-signups">New User Signups</Label>
                    <p className="text-xs text-muted-foreground">
                      Get notified when new users sign up
                    </p>
                  </div>
                  <Switch
                    id="push-signups"
                    checked={pushSettings.notifications?.signups || false}
                    onCheckedChange={(checked) => updateSettings(['push', 'notifications', 'signups'], checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="push-bookings">New Bookings</Label>
                    <p className="text-xs text-muted-foreground">
                      Get notified when new bookings are made
                    </p>
                  </div>
                  <Switch
                    id="push-bookings"
                    checked={pushSettings.notifications?.bookings || false}
                    onCheckedChange={(checked) => updateSettings(['push', 'notifications', 'bookings'], checked)}
                  />
                </div>
              </div>

              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Note:</strong> Admins need to enable push notifications in their browser and have the PWA installed or the website open to receive notifications.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Push Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Test Push Notifications
          </CardTitle>
          <CardDescription>
            Send a test push notification to verify your configuration is working correctly.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border rounded-lg space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium">Test Notification</h4>
              <p className="text-sm text-muted-foreground">
                This will send a test notification to your current browser if you have push notifications enabled.
              </p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={enablePushNotifications}
                disabled={!user || !vapidStatus.isConfigured}
                className="w-full"
                variant="default"
              >
                <Bell className="mr-2 h-4 w-4" />
                Register This Device
              </Button>
              
              <Button
                onClick={checkDeviceRegistration}
                disabled={!user || isCheckingRegistration}
                className="w-full"
                variant="outline"
              >
                {isCheckingRegistration ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Check Device Registration
                  </>
                )}
              </Button>
              
              <Button
                onClick={testSigningAndEncryption}
                disabled={!user || isTestingSigning || !vapidStatus.isConfigured}
                className="w-full"
                variant="outline"
              >
                {isTestingSigning ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-4 w-4" />
                    Sign and Encrypt
                  </>
                )}
              </Button>
              
              <Button
                onClick={async () => {
                  // Give database time to sync after registration
                  setTimeout(() => sendTestPushNotification(), 2000);
                }}
                disabled={!user || isSendingTestPush || !vapidStatus.isConfigured}
                className="w-full"
                variant="outline"
              >
                {isSendingTestPush ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Bell className="mr-2 h-4 w-4" />
                    Test Push Notification
                  </>
                )}
              </Button>
              
              <Button
                onClick={sendAdminBroadcast}
                disabled={!user || isSendingTestPush || !vapidStatus.isConfigured}
                className="w-full"
              >
                {isSendingTestPush ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Sending broadcast...
                  </>
                ) : (
                  <>
                    <Bell className="mr-2 h-4 w-4" />
                    Send to All My Devices
                  </>
                )}
              </Button>

              <Button
                onClick={clearPushSubscription}
                disabled={!user || isSendingTestPush}
                className="w-full"
                variant="ghost"
                size="sm"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Clear & Reset Push Notifications
              </Button>
            </div>

            {!vapidStatus.isConfigured && (
              <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <AlertCircle className="inline h-4 w-4 mr-1" />
                  {hasSupabaseVAPIDKeys === null ? 
                    "Checking VAPID keys configuration..." :
                    "VAPID keys required. They are already configured in Supabase secrets or generate new ones locally."
                  }
                </p>
              </div>
            )}

            {vapidStatus.isConfigured && (
              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                <h5 className="font-medium text-blue-800 dark:text-blue-300 mb-2">
                  Push Notification Setup Steps:
                </h5>
                <ol className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
                  <li>Enable push notifications when prompted by your browser/PWA</li>
                  <li>Make sure you're using the PWA (installed app) or have the website open</li>
                  <li>Click the test button below to send a notification</li>
                </ol>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  <strong>Note:</strong> If you don't see the browser permission prompt, check your browser's notification settings.
                </p>
              </div>
            )}

            {!user && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-800 dark:text-red-300">
                  <XCircle className="inline h-4 w-4 mr-1" />
                  You must be logged in to send test notifications.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manifest Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            PWA Manifest Settings
          </CardTitle>
          <CardDescription>
            Configure your Progressive Web App manifest settings including app details, colors, and icons.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* App Information */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">App Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="appName">App Name</Label>
                <Input
                  id="appName"
                  value={pwaSettings.manifest.name}
                  onChange={(e) => updateSettings(['pwa', 'manifest', 'name'], e.target.value)}
                  placeholder="LamuLamu - Local Services Platform"
                />
              </div>

              <div>
                <Label htmlFor="shortName">Short Name</Label>
                <Input
                  id="shortName"
                  value={pwaSettings.manifest.shortName}
                  onChange={(e) => updateSettings(['pwa', 'manifest', 'shortName'], e.target.value)}
                  placeholder="LamuLamu"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={pwaSettings.manifest.description}
                onChange={(e) => updateSettings(['pwa', 'manifest', 'description'], e.target.value)}
                placeholder="Find and book local services in your area"
                rows={3}
              />
            </div>
          </div>

          {/* Colors */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">App Colors</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="themeColor">Theme Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="themeColor"
                    type="color"
                    value={pwaSettings.manifest.themeColor}
                    onChange={(e) => updateSettings(['pwa', 'manifest', 'themeColor'], e.target.value)}
                    className="w-16 h-10 p-1 rounded border"
                  />
                  <Input
                    value={pwaSettings.manifest.themeColor}
                    onChange={(e) => updateSettings(['pwa', 'manifest', 'themeColor'], e.target.value)}
                    placeholder="#91DA73"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Color of the browser UI when app is running
                </p>
              </div>

              <div>
                <Label htmlFor="backgroundColor">Background Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="backgroundColor"
                    type="color"
                    value={pwaSettings.manifest.backgroundColor}
                    onChange={(e) => updateSettings(['pwa', 'manifest', 'backgroundColor'], e.target.value)}
                    className="w-16 h-10 p-1 rounded border"
                  />
                  <Input
                    value={pwaSettings.manifest.backgroundColor}
                    onChange={(e) => updateSettings(['pwa', 'manifest', 'backgroundColor'], e.target.value)}
                    placeholder="#ffffff"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Background color for the splash screen
                </p>
              </div>
            </div>
          </div>

          {/* App Icons */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">App Icons</h4>
            
            <div className="grid gap-4">
              <div className="flex items-center justify-between p-4 bg-secondary/10 rounded-lg">
                <div>
                  <h4 className="font-medium">Icon Status Overview</h4>
                  <p className="text-sm text-muted-foreground">
                    {availableIcons.length} of {requiredIcons.length} required icon sizes uploaded
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">{Math.round((availableIcons.length / requiredIcons.length) * 100)}%</div>
                  <div className="text-xs text-muted-foreground">Complete</div>
                </div>
              </div>

              {/* Icon Generation Controls */}
              {pwaSettings.customIcons.icon512 && missingIcons.length > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-blue-800 dark:text-blue-300">Generate Missing Sizes</h5>
                      <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                        Generate {missingIcons.length} missing icon sizes from your 512x512 image
                      </p>
                      {generationProgress && (
                        <div className="mt-2">
                          <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                            Generating {generationProgress.current} of {generationProgress.total}...
                          </div>
                          <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                            <div 
                              className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(generationProgress.current / generationProgress.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handleGenerateMissingIcons}
                      disabled={isGenerating || !pwaSettings.customIcons.icon512}
                      size="sm"
                      className="ml-4"
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Image className="h-4 w-4 mr-2" />
                          Generate Icons
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Icon Generation Test Controls */}
              <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className="font-medium text-purple-800 dark:text-purple-300">Test Icon Generation</h5>
                    <p className="text-sm text-purple-700 dark:text-purple-400 mt-1">
                      Test the icon generator with sample sizes (72, 96, 128, 192px) using your 512x512 icon or placeholder
                    </p>
                    {generationProgress && (
                      <div className="mt-2">
                        <div className="text-xs text-purple-600 dark:text-purple-400 mb-1">
                          Testing generation {generationProgress.current} of {generationProgress.total}...
                        </div>
                        <div className="w-full bg-purple-200 dark:bg-purple-800 rounded-full h-2">
                          <div 
                            className="bg-purple-600 dark:bg-purple-400 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(generationProgress.current / generationProgress.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleTestIconGeneration}
                    disabled={isGenerating}
                    size="sm"
                    variant="outline"
                    className="ml-4"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <Bug className="h-4 w-4 mr-2" />
                        Test Generation
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {!pwaSettings.customIcons.icon512 && missingIcons.length > 0 && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                    <div>
                      <h5 className="font-medium text-yellow-800 dark:text-yellow-300">Upload 512x512 Icon First</h5>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                        Upload a 512x512 icon to automatically generate all missing sizes
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {missingCriticalIcons.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                    <div>
                      <h5 className="font-medium text-red-800">Missing Critical iPhone Icons</h5>
                      <p className="text-sm text-red-700 mt-1">
                        Missing iPhone-specific sizes: {missingCriticalIcons.join('x, ')}x
                      </p>
                      <p className="text-xs text-red-600 mt-2">
                        These sizes are essential for proper "Add to Home Screen" on iPhone devices.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {requiredIcons.map((size) => {
                const iconKey = `icon${size}` as keyof typeof pwaSettings.customIcons;
                const hasIcon = !!pwaSettings.customIcons[iconKey];
                const isIPhoneIcon = iPhoneIcons.includes(size);
                const isSourceIcon = size === '512'; // Special indicator for source icon
                const fileInputRef = iconRefs[iconKey];
                
                return (
                  <div key={size} className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{size}×{size}</span>
                      {isIPhoneIcon && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded">iPhone</span>
                      )}
                      {isSourceIcon && (
                        <span className="text-xs bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 px-1.5 py-0.5 rounded">Source</span>
                      )}
                      {hasIcon ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    
                    <div className="relative">
                      <Button
                        onClick={() => triggerFileInput(iconKey)}
                        variant={hasIcon ? "secondary" : isIPhoneIcon ? "default" : "outline"}
                        size="sm"
                        className="w-full h-16 flex flex-col items-center gap-1"
                      >
                        {hasIcon ? (
                          <>
                            <img 
                              src={pwaSettings.customIcons[iconKey]} 
                              alt={`Icon ${size}x${size}`}
                              className="w-8 h-8 object-cover rounded"
                            />
                            <span className="text-xs">Replace</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            <span className="text-xs">{isIPhoneIcon ? 'Critical' : 'Upload'}</span>
                          </>
                        )}
                      </Button>
                      
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleIconUpload(e, parseInt(size), iconKey)}
                        className="hidden"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Display Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Display Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="display">Display Mode</Label>
                <select 
                  className="w-full p-2 border rounded-md bg-background"
                  value={pwaSettings.manifest.display || 'standalone'}
                  onChange={(e) => updateSettings(['pwa', 'manifest', 'display'], e.target.value)}
                >
                  <option value="standalone">Standalone</option>
                  <option value="fullscreen">Fullscreen</option>
                  <option value="minimal-ui">Minimal UI</option>
                  <option value="browser">Browser</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  How the app appears when launched from home screen
                </p>
              </div>

              <div>
                <Label htmlFor="orientation">Orientation</Label>
                <select 
                  className="w-full p-2 border rounded-md bg-background"
                  value={pwaSettings.manifest.orientation || 'portrait'}
                  onChange={(e) => updateSettings(['pwa', 'manifest', 'orientation'], e.target.value)}
                >
                  <option value="any">Any</option>
                  <option value="natural">Natural</option>
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                  <option value="portrait-primary">Portrait Primary</option>
                  <option value="landscape-primary">Landscape Primary</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Preferred screen orientation for the app
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PWA Installation & Testing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            PWA Installation & Testing
          </CardTitle>
          <CardDescription>
            Test your PWA installation and debug any issues.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Installation Status</Label>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                {isInstalled ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm">App is installed</span>
                  </>
                ) : isInstallable ? (
                  <>
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">Ready to install</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-orange-600" />
                    <span className="text-sm">Not installable</span>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Actions</Label>
              <div className="flex gap-2">
                <Button 
                  onClick={runPWADiagnostics}
                  variant="outline" 
                  size="sm"
                  className="flex-1"
                >
                  <Bug className="h-4 w-4 mr-2" />
                  Run Diagnostics
                </Button>
                
                {isInstallable && (
                  <Button 
                    onClick={debugInstall}
                    variant="outline" 
                    size="sm"
                    className="flex-1"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Test Install
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Debug Information Display */}
          {(manifestData || swStatus || debugInfo) && (
            <div className="space-y-4 pt-4 border-t">
              <h5 className="font-medium">Debug Information</h5>
              
              {manifestData && (
                <div className="p-3 bg-muted rounded-lg">
                  <h6 className="font-medium text-sm mb-2">Manifest Data</h6>
                  <pre className="text-xs overflow-auto max-h-32">
                    {JSON.stringify(manifestData, null, 2)}
                  </pre>
                </div>
              )}
              
              {swStatus && (
                <div className="p-3 bg-muted rounded-lg">
                  <h6 className="font-medium text-sm mb-2">Service Worker Status</h6>
                  <pre className="text-xs overflow-auto max-h-32">
                    {JSON.stringify(swStatus, null, 2)}
                  </pre>
                </div>
              )}
              
              {debugInfo && (
                <div className="p-3 bg-muted rounded-lg">
                  <h6 className="font-medium text-sm mb-2">Environment Info</h6>
                  <pre className="text-xs overflow-auto max-h-32">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-6">
        <Button 
          onClick={saveSettings} 
          disabled={isSaving}
          size="lg"
        >
          {isSaving ? "Saving..." : "Save PWA Settings"}
        </Button>
      </div>
    </div>
  );
};